// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  type AppointmentTypeDefinition,
  type AvailableTimeSlot,
  resolveDefaultStaffPractitioner,
  type SchedulingEligibility,
} from '@polarissleep/scheduling';
import * as Sentry from '@sentry/react';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { getConfig, type SchedulingEngine } from '../../config';
import type { PatientAppointmentCandidate } from '../../scheduling-next/types';
import { bookAppointment, bookV2Appointment } from '../../services/booking-service';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { ensurePatientTimezone } from '../../utils/patient-timezone';
import { AppointmentTimeWithTimezone } from './AppointmentTimeWithTimezone';
import { BookingConfirmation } from './BookingConfirmation';
import { SlotPicker } from './SlotPicker';

type BookingStep = 'type-selection' | 'slot-selection' | 'success';

interface BookingFlowProps {
  eligibility: SchedulingEligibility;
  schedulingEngine?: SchedulingEngine;
}

export function BookingFlow({
  eligibility,
  schedulingEngine = getConfig().schedulingEngine,
}: BookingFlowProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const navigate = useNavigate();
  const [step, setStep] = useState<BookingStep>('type-selection');
  const [selectedType, setSelectedType] = useState<AppointmentTypeDefinition | undefined>();
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | undefined>();
  const [selectedCandidate, setSelectedCandidate] = useState<PatientAppointmentCandidate | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [v2SearchReady, setV2SearchReady] = useState(false);
  const [v2SearchError, setV2SearchError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [defaultPractitionerId, setDefaultPractitionerId] = useState<string | undefined>();
  const [defaultStaffInvalid, setDefaultStaffInvalid] = useState(false);

  useEffect(() => {
    if (eligibility.allowedAppointmentTypes.length === 1) {
      setSelectedType(eligibility.allowedAppointmentTypes[0]);
      setStep('slot-selection');
    }
  }, [eligibility.allowedAppointmentTypes]);

  useEffect(() => {
    if (!selectedType) {
      return;
    }

    let active = true;
    setDefaultPractitionerId(undefined);
    setDefaultStaffInvalid(false);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setV2SearchReady(false);
    setV2SearchError(null);

    if (schedulingEngine === 'medplum-v2') {
      return;
    }

    resolveDefaultStaffPractitioner(medplum, selectedType.coding.code ?? '')
      .then((result): void => {
        if (!active) {
          return;
        }

        if (result.status === 'resolved') {
          setDefaultPractitionerId(result.practitionerId);
          return;
        }

        setDefaultStaffInvalid(true);
        const configError = new Error(
          `Invalid appointment default staff config for appointment type ${selectedType.coding.code ?? 'unknown'}`
        );
        Sentry.captureException(configError, {
          extra: {
            appointmentTypeCode: selectedType.coding.code,
            reason: result.reason,
            practitionerId: result.practitionerId,
          },
        });
      })
      .catch((err: unknown): void => {
        if (!active) {
          return;
        }
        setDefaultStaffInvalid(true);
        Sentry.captureException(err, {
          extra: { appointmentTypeCode: selectedType.coding.code },
        });
      });

    return () => {
      active = false;
    };
  }, [medplum, schedulingEngine, selectedType]);

  function handleTypeSelected(type: AppointmentTypeDefinition): void {
    setSelectedType(type);
    setSelectedSlot(undefined);
    setSelectedCandidate(undefined);
    setDefaultStaffInvalid(false);
    setDefaultPractitionerId(undefined);
    setStep('slot-selection');
  }

  const handleV2SearchStateChange = useCallback(({ ready, error }: { ready: boolean; error: Error | null }): void => {
    setV2SearchReady(ready);
    setV2SearchError(error);
    if (!ready || error) {
      setSelectedCandidate(undefined);
      setShowConfirmation(false);
    }
  }, []);

  async function handleConfirm(): Promise<void> {
    if (!selectedType || !profile?.id) {
      return;
    }
    if (schedulingEngine === 'legacy' && !selectedSlot) {
      return;
    }
    if (schedulingEngine === 'medplum-v2' && !selectedCandidate) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const patient = await ensurePatientTimezone(medplum, profile);
      if (schedulingEngine === 'medplum-v2' && selectedCandidate) {
        const result = await bookV2Appointment(medplum, {
          patient,
          candidate: selectedCandidate,
          appointmentType: selectedType,
        });
        setShowConfirmation(false);
        if (result.requiresPayment) {
          navigate(`/pay/${result.appointmentId}`);
        } else {
          setStep('success');
        }
        return;
      }

      if (!selectedSlot) {
        return;
      }
      const result = await bookAppointment(medplum, {
        patient,
        slotId: selectedSlot.slotId,
        appointmentTypeCode: selectedType.coding.code ?? '',
        providerId: selectedSlot.providerId,
        start: selectedSlot.start,
        end: selectedSlot.end,
        appointmentType: selectedType,
      });

      setShowConfirmation(false);
      if (result.requiresPayment) {
        navigate(`/pay/${result.appointmentId}`);
      } else {
        setStep('success');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (step === 'type-selection') {
    return (
      <Stack gap="md">
        <Title order={3}>Select Appointment Type</Title>
        <Text size="sm" c="dimmed">
          Based on your care stage, the following appointment types are available:
        </Text>

        {eligibility.allowedAppointmentTypes.map((type) => (
          <Card
            key={type.coding.code}
            withBorder
            p="md"
            radius="md"
            style={{ cursor: 'pointer' }}
            onClick={() => handleTypeSelected(type)}
          >
            <Group justify="space-between">
              <Stack gap={4}>
                <Text fw={500}>{type.display}</Text>
                <Text size="sm" c="dimmed">
                  {type.durationMinutes} min • {type.mode === 'virtual' ? 'Virtual' : 'In-Person'}
                </Text>
              </Stack>
              <Button variant="light" size="sm">
                Select
              </Button>
            </Group>
          </Card>
        ))}
      </Stack>
    );
  }

  if (step === 'slot-selection' && selectedType) {
    return (
      <Stack gap="lg">
        {error && (
          <Alert color="red" title="Booking failed">
            {error}
          </Alert>
        )}

        {defaultStaffInvalid && (
          <Alert color="yellow" title="Default staff unavailable">
            Default staff is unavailable. Showing appointments with all eligible providers.
          </Alert>
        )}

        {(defaultStaffInvalid || defaultPractitionerId) && schedulingEngine === 'legacy' && (
          <SlotPicker
            appointmentType={selectedType}
            practitionerId={defaultStaffInvalid ? undefined : defaultPractitionerId}
            selectedSlot={selectedSlot}
            onSlotSelected={setSelectedSlot}
          />
        )}

        {!defaultStaffInvalid && schedulingEngine === 'medplum-v2' && (
          <SlotPicker
            engine="medplum-v2"
            appointmentType={selectedType}
            selectedSlot={selectedCandidate}
            onSlotSelected={setSelectedCandidate}
            onSearchStateChange={handleV2SearchStateChange}
          />
        )}

        <Group justify="center" mt="md">
          <Button
            variant="outline"
            onClick={() => {
              if (eligibility.allowedAppointmentTypes.length > 1) {
                setSelectedType(undefined);
                setSelectedSlot(undefined);
                setDefaultPractitionerId(undefined);
                setDefaultStaffInvalid(false);
                setStep('type-selection');
              } else {
                navigate('/appointments');
              }
            }}
          >
            Back
          </Button>
          <Button
            disabled={
              schedulingEngine === 'medplum-v2'
                ? !selectedCandidate || !v2SearchReady || Boolean(v2SearchError)
                : !selectedSlot
            }
            onClick={() => setShowConfirmation(true)}
          >
            Book Appointment
          </Button>
        </Group>

        {schedulingEngine === 'legacy' && selectedSlot && (
          <BookingConfirmation
            opened={showConfirmation}
            appointmentType={selectedType}
            slot={selectedSlot}
            onConfirm={handleConfirm}
            onBack={() => setShowConfirmation(false)}
            isSubmitting={isSubmitting}
          />
        )}

        {schedulingEngine === 'medplum-v2' && selectedCandidate && (
          <BookingConfirmation
            opened={showConfirmation}
            appointmentType={selectedType}
            slot={selectedCandidate}
            onConfirm={handleConfirm}
            onBack={() => setShowConfirmation(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </Stack>
    );
  }

  const successSlot = selectedCandidate
    ? {
        start: selectedCandidate.appointment.start ?? '',
        end: selectedCandidate.appointment.end ?? '',
        providerName: selectedCandidate.providerName,
      }
    : selectedSlot;

  if (step === 'success' && selectedType && successSlot) {
    return (
      <Stack gap="md" align="center" py="xl">
        <IconCalendarCheck size={48} color="var(--mantine-color-green-6)" />
        <Title order={3}>Appointment Booked!</Title>
        <Card withBorder p="lg" radius="md" maw={400} w="100%">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Type
              </Text>
              <Text size="sm">{selectedType.display}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Date
              </Text>
              <Text size="sm">{formatAppointmentDate(successSlot.start)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Time
              </Text>
              <AppointmentTimeWithTimezone start={successSlot.start} end={successSlot.end} textProps={{ size: 'sm' }} />
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Provider
              </Text>
              <Text size="sm">{successSlot.providerName}</Text>
            </Group>
          </Stack>
        </Card>
        <Text size="sm" c="dimmed" ta="center">
          {selectedType.mode === 'virtual'
            ? 'A Zoom meeting link will be available before your appointment.'
            : 'Please arrive 10 minutes early.'}
        </Text>
        <Button
          variant="outline"
          onClick={() => {
            navigate('/appointments');
          }}
        >
          Back to Appointments
        </Button>
      </Stack>
    );
  }

  return <Text>Loading...</Text>;
}
